import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, Save, MailCheck, MailWarning, AlertTriangle, CheckCircle2,
    Receipt, Plus, ShieldOff, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as api from '@/services/apiClient';
import BillingContextPanel from '@/components/superadmin/BillingContextPanel';
import { toast } from 'sonner';

const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/**
 * How the address came to be trusted.
 *
 * Distinguished on screen because a link someone actually opened is stronger
 * evidence than an address we recognise, and flattening both to "Verified"
 * would overstate what is known.
 */
const VERIFIED_VIA_LABEL = {
    link:         'Confirmed by the recipient on',
    auto_self:    'Accepted automatically (our own domain) on',
    auto_contact: 'Accepted automatically (account signup address) on',
    auto_account: 'Accepted automatically (existing user account) on',
};

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

/**
 * Billing identity, invoices and access control for one company.
 *
 * Billing details are kept apart from the public profile on purpose: a finance
 * address is rarely the admin who signed up, and invoices sent to whoever
 * happened to register are how invoices go unpaid.
 */
export default function CompanyBillingPanel({ company, onChanged }) {
    const navigate = useNavigate();
    const companyId = company?.companyId;

    const [billing, setBilling] = useState(null);
    const [missing, setMissing] = useState([]);
    const [verified, setVerified] = useState(false);
    const [verifiedVia, setVerifiedVia] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingVerify, setSendingVerify] = useState(false);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const [b, inv, ctx] = await Promise.all([
                api.getCompanyBilling(companyId),
                api.listInvoices({ companyId }),
                // Their billing position, so the dates behind an invoice are
                // visible here too rather than only while raising an order.
                api.getOrderContext(companyId).catch(() => null),
            ]);
            setContext(ctx?.data || null);
            setBilling(b.data.billing || {});
            setMissing(b.data.missingForGst || []);
            setVerified(!!b.data.billingEmailVerified);
            setVerifiedVia(b.data.billing?.billingEmailVerifiedVia || null);
            setInvoices(inv.data || []);
        } catch (err) {
            toast.error(err.message || 'Could not load billing details.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const update = (field, value) => setBilling(b => ({ ...b, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await api.updateCompanyBilling(companyId, billing);
            setBilling(res.data.billing);
            setMissing(res.data.missingForGst || []);
            setVerified(!!res.data.billingEmailVerified);
            setVerifiedVia(res.data.billing?.billingEmailVerifiedVia || null);
            toast.success(res.message || 'Billing details saved.');
        } catch (err) {
            toast.error(err.message || 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    const handleVerify = async () => {
        setSendingVerify(true);
        try {
            const res = await api.sendBillingEmailVerification(companyId);
            toast.success(res.message);
            // An address we already reach is accepted outright, with no link.
            if (res.data?.billingEmailVerified) { setVerified(true); load(); }
        } catch (err) {
            toast.error(err.message || 'Could not send the verification link.');
        } finally {
            setSendingVerify(false);
        }
    };

    const handleSuspension = async (suspend) => {
        let reason = '';
        if (suspend) {
            reason = window.prompt(
                'Why is this company being suspended? This is shown to their users.',
            ) || '';
            if (!reason.trim()) return;
        }
        try {
            const res = await api.setCompanySuspension(companyId, suspend, reason.trim());
            toast.success(res.message);
            onChanged?.();
        } catch (err) {
            toast.error(err.message || 'Could not change access.');
        }
    };

    if (loading || !billing) {
        return (
            <div className="flex h-40 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const isSuspended = company?.subscription?.status === 'suspended' || company?.isActive === false;
    const ready = missing.length === 0;

    return (
        <div className="space-y-6">
            {context && <BillingContextPanel context={context} title="Billing position" />}

            {/* Readiness */}
            <div className={`flex items-start gap-3 rounded-xl border p-4 ${
                ready ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'
            }`}>
                {ready
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-500" />
                    : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />}
                <div className="text-sm">
                    <p className="font-medium text-foreground">
                        {ready ? 'Ready to invoice.' : 'Cannot invoice yet.'}
                    </p>
                    {!ready && (
                        <p className="mt-0.5 text-muted-foreground">
                            Still needed: <strong className="text-foreground">{missing.join(', ')}</strong>
                        </p>
                    )}
                </div>
            </div>

            {/* Billing identity */}
            <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 font-semibold text-foreground">Billing identity</h3>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <Label>Registered legal name</Label>
                        <Input value={billing.legalName || ''} onChange={e => update('legalName', e.target.value)}
                            placeholder={company?.companyName} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>GSTIN</Label>
                        <Input value={billing.gstin || ''} onChange={e => update('gstin', e.target.value.toUpperCase())}
                            placeholder="29AAPFU0939F1Z6" maxLength={15} className="mt-1.5 font-mono text-sm" />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            Decides CGST+SGST vs IGST against your own state.
                        </p>
                    </div>
                    <div>
                        <Label>PAN</Label>
                        <Input value={billing.pan || ''} onChange={e => update('pan', e.target.value.toUpperCase())}
                            maxLength={10} className="mt-1.5 font-mono text-sm" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Registered address</Label>
                        <Input value={billing.addressLine1 || ''} onChange={e => update('addressLine1', e.target.value)}
                            placeholder="Address line 1" className="mt-1.5" />
                        <Input value={billing.addressLine2 || ''} onChange={e => update('addressLine2', e.target.value)}
                            placeholder="Address line 2 (optional)" className="mt-2" />
                    </div>
                    <div>
                        <Label>City</Label>
                        <Input value={billing.city || ''} onChange={e => update('city', e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>State</Label>
                        <Input value={billing.state || ''} onChange={e => update('state', e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Pincode</Label>
                        <Input value={billing.pincode || ''} onChange={e => update('pincode', e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Billing phone</Label>
                        <Input value={billing.billingPhone || ''} onChange={e => update('billingPhone', e.target.value)} className="mt-1.5" />
                    </div>
                </div>

                <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
                    <Label>Billing email</Label>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                        <Input type="email" value={billing.billingEmail || ''}
                            onChange={e => update('billingEmail', e.target.value)}
                            placeholder="finance@company.com" className="min-w-[220px] flex-1" />
                        <Button variant="outline" onClick={handleVerify}
                            disabled={sendingVerify || verified || !billing.billingEmail} className="gap-1.5">
                            {sendingVerify ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MailCheck className="h-3.5 w-3.5" />}
                            {verified ? 'Verified' : 'Send verification'}
                        </Button>
                    </div>
                    <p className={`mt-2 flex items-start gap-1.5 text-[11px] ${verified ? 'text-emerald-600 dark:text-emerald-500' : 'text-amber-600 dark:text-amber-500'}`}>
                        {verified ? <MailCheck className="mt-px h-3 w-3 shrink-0" /> : <MailWarning className="mt-px h-3 w-3 shrink-0" />}
                        <span>
                            {verified
                                ? `${VERIFIED_VIA_LABEL[verifiedVia] || 'Confirmed'} ${fmtDate(billing.billingEmailVerifiedAt)}. Invoices can be emailed here.`
                                : 'Not verified yet — invoices cannot be emailed until someone confirms this address. Editing it clears any earlier confirmation.'}
                        </span>
                    </p>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button onClick={handleSave} disabled={saving} className="gap-2">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save billing details
                    </Button>
                </div>
            </div>

            {/* Invoices */}
            <div className="rounded-xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="font-semibold text-foreground">Invoices ({invoices.length})</h3>
                    <Button size="sm" variant="outline" className="gap-1.5"
                        onClick={() => navigate('/super-admin/billing')}>
                        <Plus className="h-3.5 w-3.5" /> New order
                    </Button>
                </div>

                {invoices.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        Nothing invoiced yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left">
                                    <th className="pb-2 font-medium text-muted-foreground">Invoice</th>
                                    <th className="pb-2 text-right font-medium text-muted-foreground">Total</th>
                                    <th className="pb-2 text-right font-medium text-muted-foreground">Due</th>
                                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                                    <th className="pb-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {invoices.map(inv => (
                                    <tr key={inv._id}>
                                        <td className="py-2.5 font-mono text-xs">{inv.invoiceNumber}</td>
                                        <td className="py-2.5 text-right tabular-nums">{money(inv.total)}</td>
                                        <td className="py-2.5 text-right tabular-nums">{money(inv.amountDue)}</td>
                                        <td className="py-2.5 capitalize text-muted-foreground">
                                            {inv.status.replace('_', ' ')}
                                        </td>
                                        <td className="py-2.5 text-right">
                                            <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                                                onClick={() => api.downloadInvoicePdf(inv._id, inv.invoiceNumber)
                                                    .catch(e => toast.error(e.message))}>
                                                <Receipt className="h-3 w-3" /> PDF
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Access control */}
            <div className={`rounded-xl border p-5 ${isSuspended ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'}`}>
                <h3 className="font-semibold text-foreground">Account access</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                    {isSuspended
                        ? 'This company is suspended. Its users cannot sign in, and interviews in progress are stopped.'
                        : 'Suspension is a hard stop — unlike an expired subscription, it also halts interviews already scheduled. Use it only when access must end immediately.'}
                </p>
                {isSuspended && company?.suspensionReason && (
                    <p className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-foreground">
                        <strong>Reason shown to them:</strong> {company.suspensionReason}
                    </p>
                )}
                <div className="mt-4">
                    {isSuspended ? (
                        <Button variant="outline" className="gap-2" onClick={() => handleSuspension(false)}>
                            <ShieldCheck className="h-4 w-4" /> Restore access
                        </Button>
                    ) : (
                        <Button variant="outline" className="gap-2 text-destructive hover:text-destructive"
                            onClick={() => handleSuspension(true)}>
                            <ShieldOff className="h-4 w-4" /> Suspend company
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
